import { inject, Injector, runInInjectionContext } from '@angular/core'

import { firstValueFrom } from 'rxjs'

import { TranslateService } from '@ngx-translate/core'

import { LocalePinService } from './locale-pin.service'
import { AUTH_STORE } from '../tokens/auth-store.token'

/**
 * Initializes language settings using TranslateService in a safe DI context.
 * Should be called after bootstrapApplication.
 *
 * Resolves the account's language: `user.language`, then `localStorage['lang']`,
 * then the browser's. An app that pins the language to something other than the
 * account (see {@link LocalePinService}) overrides all three; if such a pin has
 * already landed by the time this runs, we leave it alone.
 */
export async function initializeLanguage(injector: Injector, defaultLang = 'en') {
  await runInInjectionContext(injector, async () => {
    const translate = inject(TranslateService)
    const authStore = inject(AUTH_STORE)
    const pin = inject(LocalePinService, { optional: true })?.currentPin ?? null

    const userLang = authStore.getUserLanguage()
    const savedLang = localStorage.getItem('lang')
    const browserLang = navigator.language?.split('-')[0]
    const isBrowserLangDifferent = browserLang && browserLang !== defaultLang

    let langToUse = pin ?? defaultLang

    // Once pinned, nothing about the account gets a say.
    if (!pin) {
      if (userLang) {
        langToUse = userLang.split('-')[0]
      } else if (savedLang) {
        langToUse = savedLang.split('-')[0]
      } else if (isBrowserLangDifferent) {
        langToUse = browserLang
      }
    }

    translate.setDefaultLang(defaultLang)
    await firstValueFrom(translate.use(langToUse))

    if (localStorage.getItem('lang') !== langToUse) {
      localStorage.setItem('lang', langToUse)
    }
  })
}
